import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import usePermission from "../../../config/permissions";
import { handleErrorToast, SearchFilterInput } from "../../CustomFunctions";
import axios from "axios";
import { BASE_URL } from "../../../config/axios";
import { Eye, Office, PlayIcon, RefreshIcon } from "../../AllSvg";
import { formatDateTime } from "../../Date";
import { Modal } from "react-bootstrap";

// ── helpers ──────────────────────────────────────────────────────────────────
const MONTHS = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];


const appliedCount = (employees = []) =>
    employees.filter((e) => e.sandwich_applied).length;

// ── employee detail modal ─────────────────────────────────────────────────────
const EmployeeDetailModal = ({ run }) => {
    const [show, setShow] = useState(false);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // all | applied | not_applied

    const handleShow = () => {
        setShow(true);
    }

    const handleClose = () => {
        setShow(false);
    }

    const filtered = (run?.employees || []).filter((e) => {
        const matchSearch = e.employee_id.toLowerCase().includes(search.toLowerCase());
        const matchFilter =
            filter === "all" ||
            (filter === "applied" && e.sandwich_applied) ||
            (filter === "not_applied" && !e.sandwich_applied);
        return matchSearch && matchFilter;
    });

    return (

        <>
            <button className="upload-svg" onClick={handleShow} title="View Employees">
                View Employees
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="half-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Run #{run.run_id} — Employee Details</Modal.Title>

                </Modal.Header>
                <Modal.Body>
                    {/* filters */}
                    <div className="sandwich-modal-filters">
                        <SearchFilterInput
                            placeholder="Search employee ID…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onClear={() => setSearch("")}
                            widthClass="width-16vw"
                        />


                        <div className="sandwich-chip-group">
                            {["all", "applied", "not_applied"].map((v) => (
                                <button
                                    key={v}
                                    className={`sandwich-chip-btn ${filter === v ? "active" : ""}`}
                                    onClick={() => setFilter(v)}
                                >
                                    {v === "all" ? "All" : v === "applied" ? "Applied" : "Not Applied"}
                                </button>
                            ))}
                        </div>
                        <span className="sandwich-count-label">
                            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
                        </span>
                    </div>

                    {/* table */}
                    <div className="sandwich-modal-table-wrap">
                        <table className="table-css table-table-css">
                            <thead>
                                <tr className="custom-table-head-tr">
                                    <th className="align-left">S.No</th>
                                    <th className="align-center">Employee ID</th>
                                    <th className="align-center">Date</th>
                                    <th className="align-center">Sandwich Applied</th>
                                    <th className="align-center">Reason</th>
                                    <th className="align-center">Created By</th>
                                    <th className="align-center">Updated By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="align-center" style={{ padding: "2rem", color: "var(--color-text-secondary)" }}>
                                            No records found
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((emp, idx) => (
                                        <>
                                            <tr className="tr-border-bottom" key={`sep-${idx}`}>
                                                <td colSpan="7"></td>
                                            </tr>
                                            <tr className="custom-table-head-td custom-custom-table-head-td" key={`emp-${idx}`}>
                                                <td className="align-left">{idx + 1}</td>
                                                <td className="align-center font-weight500">{emp.employee_id}</td>
                                                <td className="align-center">
                                                    {formatDateTime(emp.date).longdate}
                                                </td>
                                                <td className="align-center">
                                                    <span className={`sandwich-chip ${emp.sandwich_applied ? "applied" : "not-applied"}`}>
                                                        {emp.sandwich_applied ? "✓ Applied" : "✗ Not Applied"}
                                                    </span>
                                                </td>
                                                <td className="align-center">{emp.reason || "—"}</td>
                                                <td className="align-center">
                                                    {emp.created_by || "—"}
                                                </td>
                                                <td className="align-center">
                                                    {emp.updated_by || "—"}
                                                </td>
                                            </tr>
                                        </>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                </Modal.Body>
            </Modal >
        </>
    );
};

// ── expandable runs sub-table ─────────────────────────────────────────────────
const RunsSubTable = ({ runs }) => {
    const [showModal, setShowModal] = useState(false);
    return (
        <tr>
            <td colSpan="9" style={{ padding: "0 0 0 2.5rem", background: "var(--color-background-secondary, #f9f9fb)" }}>
                <div className="sandwich-sub-table-wrap">
                    <p className="sandwich-sub-label font-weight500">Execution Runs</p>
                    <table className="table-css table-table-css" style={{ marginBottom: "12px" }}>
                        <thead>
                            <tr className="custom-table-head-tr">
                                <th className="align-center">Run #</th>
                                <th className="align-center">Run At</th>
                                <th className="align-center">Total Records</th>
                                <th className="align-center">Sandwich Applied</th>
                                <th className="align-center">Not Applied</th>
                                <th className="align-center">Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((run, idx) => {
                                const applied = appliedCount(run.employees);
                                const notApplied = (run.total_records || 0) - applied;

                                return (
                                    <>
                                        <tr className="tr-border-bottom" key={`rsep-${idx}`}><td colSpan="6"></td></tr>
                                        <tr className="custom-table-head-td custom-custom-table-head-td" key={`run-${idx}`}>
                                            <td className="align-center font-weight500">#{run.run_id}</td>
                                            <td className="align-center">  {formatDateTime(run.run_at).shortfull}  </td>
                                            <td className="align-center">{run.total_records}</td>
                                            <td className="align-center">
                                                <span className="sandwich-chip applied">{applied}</span>
                                            </td>
                                            <td className="align-center">
                                                <span className="sandwich-chip not-applied">{notApplied}</span>
                                            </td>
                                            <td className="align-center">
                                                {/* <button
                                                    className="model-button modal-model-button model-button-leave"
                                                    onClick={() => setShowModal(true)}
                                                    title="View employees"
                                                >
                                                    View Employees
                                                </button> */}
                                                {/* {showModal && ( */}
                                                <EmployeeDetailModal
                                                    run={run}
                                                // onClose={() => setShowModal(false)}
                                                />
                                                {/* )} */}
                                            </td>
                                        </tr>
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </td>
        </tr>
    )
}


// ── main component ────────────────────────────────────────────────────────────
const SandwichRuleLogTable = () => {
    const [toggleCompany, setToggleCompany] = useState(
        sessionStorage.getItem("company_id")
    );
    const [sandwichRuleLog, setSandwichRuleLog] = useState([]);
    const [buffer, setBuffering] = useState(true);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const { SUB_COMPANIES, SUPER_ADMIN } = usePermission();

    const getSandwichRuleLog = async () => {
        setBuffering(true);
        try {
            const res = await axios.get(`${BASE_URL}/wfm/sandwichRuleLog/`);
            setSandwichRuleLog(res.data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => {
        getSandwichRuleLog();
    }, []);

    // filter by selected sub-company
    const filteredLogs = toggleCompany && toggleCompany !== "null"
        ? sandwichRuleLog.filter((l) => l.sub_company === toggleCompany)
        : sandwichRuleLog;

    const toggleRow = (id) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const getCompanyName = (id) =>
        SUB_COMPANIES?.find((c) => c.id === id)?.title || id;

    // summary stats
    const totalRuns = filteredLogs.reduce((acc, l) => acc + (l.data?.length || 0), 0);
    const totalRecords = filteredLogs.reduce(
        (acc, l) => acc + (l.data?.[l.data.length - 1]?.total_records || 0), 0
    );

    return (
        <>
            <div style={{ margin: "10px" }} className="content-tabs">

                {/* ── filters ── */}
                {SUPER_ADMIN ? (
                    <div className="sandwichrule-field-parent-cont field-parent-cont" style={{ gap: "20px" }}>

                        <div className="sandwichrule-field-cont-div field-cont-div">
                            <Office />
                            <select
                                onChange={(e) => setToggleCompany(e.target.value)}
                                value={toggleCompany}
                                className="sandwichrule-attendance-input-field attendance-input-field width-12vw date-field"
                            >
                                <option value="null">All Sub Companies</option>
                                {SUB_COMPANIES.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.title}
                                    </option>
                                ))}
                            </select>
                            <hr className="field-cont-hr small-field-cont-hr" />
                        </div>

                    </div>
                ) : null}

                {/* ── summary cards ── */}
                <div className="sandwich-summary-row">
                    <div className="sandwich-summary-card">
                        <span className="sandwich-summary-label">Total Entries</span>
                        <span className="sandwich-summary-value">{filteredLogs.length}</span>
                    </div>
                    <div className="sandwich-summary-card">
                        <span className="sandwich-summary-label">Total Runs</span>
                        <span className="sandwich-summary-value">{totalRuns}</span>
                    </div>
                    <div className="sandwich-summary-card">
                        <span className="sandwich-summary-label">Total Records Processed</span>
                        <span className="sandwich-summary-value">{totalRecords.toLocaleString()}</span>
                    </div>
                </div>

                {/* ── table ── */}
                <div className="table-css-white-background">
                    <div className="table-heading-flex">
                        <div className="repo-heading font-weight500 font-size-heading">
                            Sandwich Rule Execution Log
                        </div>
                        <button
                            className="model-button modal-model-button model-button-leave"
                            onClick={getSandwichRuleLog}
                            title="Refresh"
                            style={{ display: "flex", alignItems: "center", gap: "6px" }}
                        >
                            <RefreshIcon size={14} /> Refresh
                        </button>
                    </div>

                    <table className="table-css table-table-css">
                        <thead>
                            <tr className="custom-table-head-tr">
                                <th className="align-left" style={{ width: "40px" }}></th>
                                <th className="align-left">S.No</th>
                                <th className="align-center">Sub Company</th>
                                <th className="align-center">Month</th>
                                <th className="align-center">Year</th>
                                <th className="align-center">No. of Runs</th>
                                <th className="align-center">Last Run At</th>
                                <th className="align-center">Total Records</th>
                                <th className="align-center">Created At</th>
                            </tr>
                        </thead>

                        {buffer ? (
                            <tbody>
                                <tr>
                                    <td colSpan="9" className="align-center" style={{ padding: "2.5rem" }}>
                                        <div className="spinner"></div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {filteredLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="align-center" style={{ padding: "2.5rem", color: "var(--color-text-secondary)" }}>
                                            No sandwich rule logs found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLogs
                                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                        .map((log, index) => {
                                            const isExpanded = expandedRows.has(log.id);
                                            const latestRun = log.data?.[log.data.length - 1];
                                            return (
                                                <>
                                                    <tr className="tr-border-bottom" key={`sep-${log.id}`}>
                                                        <td colSpan="9"></td>
                                                    </tr>

                                                    {/* main row */}
                                                    <tr
                                                        className="custom-table-head-td custom-custom-table-head-td"
                                                        key={log.id}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => toggleRow(log.id)}
                                                    >
                                                        {/* expand toggle */}
                                                        <td className="align-center" style={{ width: "40px" }}>
                                                            <span
                                                                className={`sandwich-expand-icon ${isExpanded ? "open" : ""}`}
                                                            >
                                                                <PlayIcon />
                                                            </span>
                                                        </td>

                                                        <td className="align-left">{index + 1}</td>

                                                        <td className="align-center font-weight500">
                                                            {getCompanyName(log.sub_company)}
                                                        </td>

                                                        <td className="align-center">
                                                            <span className="sandwich-chip month-chip">
                                                                {MONTHS[log.month] || log.month}
                                                            </span>
                                                        </td>

                                                        <td className="align-center">{log.year}</td>

                                                        <td className="align-center">
                                                            <span className="sandwich-chip runs-chip">
                                                                {log.data?.length || 0} run{log.data?.length !== 1 ? "s" : ""}
                                                            </span>
                                                        </td>

                                                        <td className="align-center">
                                                            {formatDateTime(latestRun?.run_at).shortfull}
                                                        </td>

                                                        <td className="align-center font-weight500">
                                                            {latestRun?.total_records?.toLocaleString() || "—"}
                                                        </td>

                                                        <td className="align-center">
                                                            {/* {newFormateDateandTime(log.created_at)} */}
                                                            {formatDateTime(log?.created_at).shortfull}
                                                        </td>
                                                    </tr>

                                                    {/* expanded runs sub-table */}
                                                    {isExpanded && log.data?.length > 0 && (
                                                        <RunsSubTable
                                                            key={`runs-${log.id}`}
                                                            runs={log.data}
                                                        />
                                                    )}
                                                </>
                                            );
                                        })
                                )}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>

            <ToastContainer
                position="top-center"
                autoClose={1000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

export default SandwichRuleLogTable;
